import { useCallback, useEffect, useState } from 'react';
import {
  fetchCameraActivity,
  saveIncident,
  type ActivityEpisode,
  type ActivityState,
  type CameraActivity as Data,
  type IncidentNote,
} from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Camera Activity Timeline di layar (§G13, §D7.4).
 *
 * Rangkaian tegak, bukan tabel: yang dibaca orang di sini "kamera ini kenapa,
 * sejak kapan, sudah berapa lama" — dan lamanya adalah kolom yang dipindai
 * mata, jadi ia rata kanan dan tidak pernah diselipkan di tengah kalimat.
 *
 * **Tidak ada `stderr` satu baris pun di sini.** Yang ingin melihatnya
 * berpindah ke tab Teknis — dan tautan per episode membawanya ke sana dengan
 * filter yang sudah terpasang pada waktu episode itu, bukan ke puncak tabel.
 */

/**
 * Warna dan bentuk bulatan mengikuti §G3.1 — kosakata yang sama dengan tile dan
 * chip level. Bentuknya ikut berbeda supaya tidak bergantung warna saja:
 * status tidak boleh disampaikan hanya lewat warna (§G3.1).
 */
const KEADAAN: Record<ActivityState, { warna: string; bentuk: string; label: string }> = {
  online: { warna: 'bg-green-600', bentuk: '●', label: 'Connected' },
  offline: { warna: 'bg-[var(--color-text-muted)]', bentuk: '○', label: 'Disconnected' },
  reconnecting: { warna: 'bg-amber-500', bentuk: '◐', label: 'Reconnecting' },
  problem: { warna: 'bg-red-600', bentuk: '▲', label: 'Problem' },
  maintenance: { warna: 'bg-accent', bentuk: '▣', label: 'Under maintenance' },
  unstable: { warna: 'bg-amber-500', bentuk: '◈', label: 'Unstable' },
};

/**
 * Lama dalam kata, bukan detik mentah.
 *
 * "offline" tanpa angka tidak membedakan tersendat empat detik dari mati empat
 * hari, dan justru bedanya itu yang menentukan apakah seseorang perlu berangkat
 * ke lokasi (§D7.4).
 */
type Terjemah = ReturnType<typeof useBahasa>['t'];

function lama(ms: number, t: Terjemah): string {
  const detik = Math.max(0, Math.round(ms / 1000));
  if (detik < 60) return t('{n}s', { n: detik });
  const menit = Math.floor(detik / 60);
  if (menit < 60) return t('{n} min', { n: menit });
  const jam = Math.floor(menit / 60);
  const sisaMenit = menit % 60;
  if (jam < 24) {
    return sisaMenit === 0 ? t('{n}h', { n: jam }) : t('{h}h {m}m', { h: jam, m: sisaMenit });
  }
  const hari = Math.floor(jam / 24);
  const sisaJam = jam % 24;
  return sisaJam === 0 ? t('{n}d', { n: hari }) : t('{d}d {h}h', { d: hari, h: sisaJam });
}

function jam(iso: string): string {
  return new Date(iso).toLocaleTimeString('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Jakarta',
  });
}

function tanggal(iso: string): string {
  return new Date(iso).toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    timeZone: 'Asia/Jakarta',
  });
}

function Baris({
  ep,
  onLihatTeknis,
}: {
  ep: ActivityEpisode;
  onLihatTeknis?: (ep: ActivityEpisode) => void;
}) {
  const { t } = useBahasa();
  const [terbentang, setTerbentang] = useState(false);
  const k = ep.state ? KEADAAN[ep.state] : null;

  // Kejadian sekejap tidak pernah mengklaim durasi — penggantian perangkat
  // berlangsung sedetik, dan menuliskannya "berlangsung 3 jam" adalah bohong
  // yang terlihat masuk akal.
  const instan = ep.kind === 'instant';

  return (
    <li className="border-b border-line last:border-b-0">
      <div className="flex items-baseline gap-3 py-2">
        <span
          aria-hidden
          className={`mt-1 inline-block h-2.5 w-2.5 shrink-0 rounded-full ${k ? k.warna : 'bg-[var(--color-text-muted)]'}`}
        />
        <span className="w-12 shrink-0 font-mono text-xs text-muted" title={tanggal(ep.startedAt)}>
          {jam(ep.startedAt)}
        </span>

        <span className="min-w-0 flex-1">
          <span className="text-sm">
            {k ? t(k.label) : t('Event')}
            {ep.state === 'reconnecting' && ep.occurrences > 1 && (
              <span className="text-muted"> — {t('{n} attempts', { n: ep.occurrences })}</span>
            )}
            {ep.state === 'unstable' && (
              <span className="text-muted"> — {t('{n} changes', { n: ep.occurrences })}</span>
            )}
          </span>
          {/* Pesannya datang dari server dan dari ffmpeg; isi log TIDAK
              diterjemahkan (§H9). Yang diterjemahkan cuma kerangkanya. */}
          {ep.message && <span className="block truncate text-xs text-muted">{ep.message}</span>}
          {ep.startedBeforeWindow && (
            <span className="block text-xs text-muted">
              {t('Started before this range, on {tgl} {jam}', {
                tgl: tanggal(ep.startedAt),
                jam: jam(ep.startedAt),
              })}
            </span>
          )}
        </span>

        <span className="shrink-0 text-right text-xs text-muted">
          {instan ? (
            ''
          ) : ep.ongoing ? (
            // "Berlangsung" adalah isi terpenting baris teratas; memberinya
            // waktu akhir yang belum terjadi akan membuatnya terbaca selesai.
            <span>{t('ongoing, {lama}', { lama: lama(ep.durationMs, t) })}</span>
          ) : (
            lama(ep.durationMs, t)
          )}
        </span>
      </div>

      {(ep.collapsed?.length ?? 0) > 0 && (
        <div className="pb-2 pl-8">
          <button
            type="button"
            onClick={() => setTerbentang((b) => !b)}
            aria-expanded={terbentang}
            className="text-xs underline underline-offset-2"
          >
            {terbentang ? t('Hide the changes') : t('Show the changes')}
          </button>
          {terbentang && (
            <ul className="mt-1 border-l border-line pl-3">
              {ep.collapsed!.map((c, i) => (
                <li key={`${c.startedAt}-${i}`} className="py-0.5 text-xs text-muted">
                  <span className="font-mono">{jam(c.startedAt)}</span>{' '}
                  {c.state ? t(KEADAAN[c.state].label) : c.event}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {onLihatTeknis && !instan && (
        <div className="pb-2 pl-8">
          <button
            type="button"
            onClick={() => onLihatTeknis(ep)}
            className="text-xs text-accent-text underline underline-offset-2"
          >
            {t('Open the technical log at this time')}
          </button>
        </div>
      )}
    </li>
  );
}

/**
 * Pita cerita manusia di atas rangkaian keadaan (§D11, §G13).
 *
 * Di sinilah bacaannya jadi utuh: mesin bilang "terputus 17 menit", orang bilang
 * "saya yang mencabutnya". Yang masih berlangsung ditandai — itu yang dibaca
 * saat pergantian shift.
 */
function PitaIncident({
  note,
  onUbah,
}: {
  note: IncidentNote;
  onUbah: () => Promise<void> | void;
}) {
  const { t } = useBahasa();
  const [sibuk, setSibuk] = useState(false);

  return (
    <li className="border-l-2 border-accent bg-subtle/60 px-3 py-2">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-xs text-muted">{jam(note.startedAt)}</span>
        <span className="text-sm">{note.title}</span>
        {note.ongoing ? (
          <span className="rounded border border-accent px-1 text-xs text-accent-text">
            {t('ongoing')}
          </span>
        ) : (
          <span className="text-xs text-muted">
            {t('until {jam}', { jam: jam(note.endedAt!) })}
          </span>
        )}
        <span className="ml-auto text-xs text-muted">{note.author}</span>
      </div>
      {note.body && <p className="mt-1 whitespace-pre-wrap text-xs text-muted">{note.body}</p>}
      {note.ongoing && (
        <button
          type="button"
          disabled={sibuk}
          onClick={() =>
            void (async () => {
              setSibuk(true);
              try {
                await saveIncident(note.id, { endedAt: new Date().toISOString() });
                await onUbah();
              } finally {
                setSibuk(false);
              }
            })()
          }
          className="mt-1 text-xs underline underline-offset-2"
        >
          {t('Mark as finished')}
        </button>
      )}
    </li>
  );
}

/** Kotak catatan baru — muncul hanya kalau diminta, bukan permanen di layar. */
function CatatanBaru({
  cameraId,
  onSimpan,
}: {
  cameraId: string;
  onSimpan: () => Promise<void> | void;
}) {
  const { t } = useBahasa();
  const [buka, setBuka] = useState(false);
  const [judul, setJudul] = useState('');
  const [isi, setIsi] = useState('');
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);

  if (!buka) {
    return (
      <button
        type="button"
        onClick={() => setBuka(true)}
        className="text-sm text-accent-text underline underline-offset-2"
      >
        {t('Add an incident note')}
      </button>
    );
  }

  return (
    <form
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (judul.trim() === '') return;
        void (async () => {
          setSibuk(true);
          setGalat(null);
          try {
            // `startedAt` sengaja tidak diisi: bawaannya "sekarang", dan
            // pekerjaan yang dicatat sambil dikerjakan adalah kasus yang paling
            // sering. Yang mencatat kejadian kemarin menyuntingnya sesudahnya.
            await saveIncident(null, { cameraId, title: judul.trim(), body: isi.trim() || null });
            setJudul('');
            setIsi('');
            setBuka(false);
            await onSimpan();
          } catch (e2) {
            setGalat((e2 as Error).message);
          } finally {
            setSibuk(false);
          }
        })();
      }}
    >
      {galat && (
        <p role="alert" className="rounded-md border border-red-600 px-3 py-2 text-sm">
          {galat}
        </p>
      )}
      <input
        value={judul}
        onChange={(e) => setJudul(e.target.value)}
        maxLength={180}
        placeholder={t('What happened? e.g. PoE switch on floor 2 replaced')}
        className="min-h-tap w-full rounded-md border border-line bg-page px-3 text-sm"
      />
      {/* Judul wajib, isi opsional (§D11): judul yang muncul di timeline dan di
          daftar; isi dibaca yang membuka. Catatan tanpa judul akan berakhir
          sebagai daftar paragraf yang harus dibaca seluruhnya untuk mencari satu. */}
      <textarea
        value={isi}
        onChange={(e) => setIsi(e.target.value)}
        rows={3}
        placeholder={t('Details (optional)')}
        className="w-full rounded-md border border-line bg-page px-3 py-2 text-sm"
      />
      <div className="flex gap-2">
        <button
          type="submit"
          disabled={sibuk || judul.trim() === ''}
          className="min-h-tap rounded-md bg-accent px-4 text-sm text-accent-contrast disabled:opacity-50"
        >
          {sibuk ? t('Saving…') : t('Save note')}
        </button>
        <button
          type="button"
          onClick={() => setBuka(false)}
          className="min-h-tap rounded-md border border-line px-4 text-sm"
        >
          {t('Cancel')}
        </button>
      </div>
    </form>
  );
}

export function CameraActivity({
  cameraId,
  hours,
  bolehCatat = false,
  onLihatTeknis,
}: {
  cameraId: string;
  /** Rentang mengikuti filter halaman — filternya milik halaman, bukan tab (§G13). */
  hours: number;
  /** Admin + Operator (§D11). Viewer tidak pernah melihat lapisan ini. */
  bolehCatat?: boolean;
  onLihatTeknis?: (ep: ActivityEpisode) => void;
}) {
  const { t } = useBahasa();
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [memuat, setMemuat] = useState(true);

  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const from = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      setData(await fetchCameraActivity(cameraId, { from }));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setMemuat(false);
    }
  }, [cameraId, hours]);

  useEffect(() => {
    void muat();
  }, [muat]);

  if (memuat && !data) return <p className="p-4 text-sm text-muted">{t('Loading activity…')}</p>;
  if (error)
    return (
      <p role="alert" className="p-4 text-sm text-amber-600">
        {error}
      </p>
    );
  if (!data) return null;

  const berjalan = data.episodes.find((e) => e.ongoing && e.kind === 'state');

  return (
    <div className="rounded-lg border border-line bg-surface">
      <div className="border-b border-line px-4 py-3">
        <h2 className="text-base">{data.camera.name}</h2>
        {/* Kamera yang sehat adalah kamera yang tidak menghasilkan baris, dan
            halaman yang menjawabnya dengan "tidak ada data" membuat orang
            mengira pencatatannya yang rusak (§D7.4). */}
        {data.quiet && berjalan && (
          <p className="mt-1 text-sm text-muted">
            {t('{keadaan} without a break since {tgl} {jam} — {lama}.', {
              keadaan: t(KEADAAN[berjalan.state!].label),
              tgl: tanggal(berjalan.startedAt),
              jam: jam(berjalan.startedAt),
              lama: lama(berjalan.durationMs, t),
            })}
          </p>
        )}
      </div>

      {(data.incidents.length > 0 || bolehCatat) && (
        <div className="border-b border-line px-4 py-3">
          {data.incidents.length > 0 && (
            <ul className="mb-2 space-y-1">
              {data.incidents.map((n) => (
                <PitaIncident key={n.id} note={n} onUbah={muat} />
              ))}
            </ul>
          )}
          {bolehCatat && <CatatanBaru cameraId={cameraId} onSimpan={muat} />}
        </div>
      )}

      {data.episodes.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted">
          {new Date(data.camera.createdAt) > new Date(data.from)
            ? t('This camera was added on {tgl} and has not connected yet.', {
                tgl: tanggal(data.camera.createdAt),
              })
            : t('Nothing changed in this range.')}
        </p>
      ) : (
        <ul className="px-4">
          {/* Terbaru di atas: yang sedang berlangsung adalah yang dicari orang
              lebih dulu, dan menaruhnya di dasar berarti menggulung untuk
              menemukan keadaan sekarang. */}
          {[...data.episodes].reverse().map((ep, i) => (
            <Baris key={`${ep.startedAt}-${i}`} ep={ep} onLihatTeknis={onLihatTeknis} />
          ))}
        </ul>
      )}

      {data.truncated && (
        <p className="border-t border-line px-4 py-2 text-xs text-muted">
          {t('This range has more events than can be shown. Narrow the time range.')}
        </p>
      )}
    </div>
  );
}
